'use client';

import { useEffect, useState, type ReactNode } from "react";
import { getThemeClass, useTheme } from "./theme";
import { Dialog, DialogBody } from "@blueprintjs/core";
import type { GitLog } from "../../src/types/gitlog";
import ChangeLogEntry from "./changelogentry";
import { SocketState, useSocket } from "./socket";
import { ClientRequests } from "../../src/enum/socket";
import type { UserProfileUser, UserProfilesResponse } from "../../src/models/socket/profile";

type AuthorProfiles = {
    [name: string]: UserProfileUser | null;
};

const AUTHOR_DATA: Record<string, number> = {
    chad: 1,
    cwalker: 1,
    aportner: 4,
    ckirmse: 2,
    chris: 2,
};

const EMPTY_AUTHOR_PROFILES: AuthorProfiles = Object.fromEntries(
    Object.keys(AUTHOR_DATA).map((name) => [name, null]),
);

type Props = {
    entries: GitLog;
    isOpen: boolean;
    onClose: () => void;
};

export default function ChangeLogDialog({
    entries,
    isOpen,
    onClose,
}: Props): ReactNode {
    const { theme } = useTheme();
    const { socket, state } = useSocket();
    const [authors, setAuthors] = useState<AuthorProfiles>(EMPTY_AUTHOR_PROFILES);

    useEffect(
        () => {
            if (!socket || state !== SocketState.CONNECTED) {
                return;
            }

            let isActive = true;

            const fetchAuthorProfiles = async (): Promise<void> => {
                try {
                    const userIds = new Set<number>(Object.values(AUTHOR_DATA));
                    const userProfiles: UserProfilesResponse = await socket.emitWithAck(
                        ClientRequests.GetUserProfiles,
                        [...userIds],
                    );

                    if (isActive) {
                        setAuthors(
                            Object.fromEntries(
                                Object.entries(AUTHOR_DATA).map(
                                    ([name, userId]) => [name, userProfiles[userId]],
                                ),
                            ),
                        );
                    }
                } catch (err) {
                    if (isActive) {
                        console.error('Error fetching changelog author profiles', err);
                    }
                }
            };

            void fetchAuthorProfiles();

            return () => {
                isActive = false;
            };
        },
        [socket, state],
    );

    const displayedAuthors = state === SocketState.CONNECTED
        ? authors
        : EMPTY_AUTHOR_PROFILES;

    const body = entries.map(
        (entry) => {
            const userProfile = displayedAuthors[entry.author];

            return <ChangeLogEntry entry={entry} userProfile={userProfile} key={entry.hash} />;
        },
    );

    return (
        <Dialog
            className={getThemeClass(theme)}
            isOpen={isOpen}
            onClose={onClose}
            style={{ minWidth: '640px' }}
            title='Change Log'
        >
            <DialogBody>
                {body}
            </DialogBody>
        </Dialog>
    );
}
